/**
 * Linked tasks under their first task (Feature 71, D33). The tasks list
 * asks the agent for first tasks only and fetches a first task's linked
 * tasks when its row is opened; the tenant home nests them from the tasks
 * it already holds. Either way the rows reach the table flat, each linked
 * task right beneath its first task, and the table tells the two apart by
 * the set of nested ids.
 */
import type { CSSProperties } from 'react';

import type { Task, TaskSource } from '@optum/api-client/types';

/** A nested row's look, the Tenant artboard's: sunken, beneath its first task. */
export const NESTED_ROW_STYLE: CSSProperties = { background: 'var(--surface-sunken)' };

/** The rows a table draws, which of them are nested, and how deep. */
export interface NestedRows {
  rows: Task[];
  nested: ReadonlySet<string>;
  /** A nested row's depth: 1 under a first task, 2 under an independent task under it (D59). */
  depth: ReadonlyMap<string, number>;
}

/**
 * Whether a list asks for first tasks only. A source filter that names
 * `linkage` is a request for linked tasks themselves, listed flat.
 */
export function listsTopLevel(sources: readonly TaskSource[]): boolean {
  return !sources.includes('linkage');
}

/** Oldest first: the order a run submitted its steps in. */
export function oldestFirst(tasks: readonly Task[]): Task[] {
  return tasks.slice().sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at));
}

/** Linked tasks grouped by their first task, each group oldest first. */
export function linkedByParent(tasks: readonly Task[]): Map<string, Task[]> {
  const groups = new Map<string, Task[]>();
  for (const task of tasks) {
    if (!task.parent_task_id) continue;
    groups.set(task.parent_task_id, [...(groups.get(task.parent_task_id) ?? []), task]);
  }
  return new Map([...groups].map(([parent, linked]) => [parent, oldestFirst(linked)]));
}

/**
 * The rows with each task's linked tasks right beneath it, and theirs beneath
 * them: a step after an independent step is that task's child (D59).
 */
export function withLinked(
  rows: readonly Task[],
  linked: ReadonlyMap<string, readonly Task[]>,
): NestedRows {
  const out: Task[] = [];
  const nested = new Set<string>();
  const depth = new Map<string, number>();
  const place = (row: Task, level: number) => {
    // A task already placed is never placed twice, whatever the data says.
    if (depth.has(row.id)) return;
    out.push(row);
    depth.set(row.id, level);
    if (level > 0) nested.add(row.id);
    for (const child of linked.get(row.id) ?? []) place(child, level + 1);
  };
  for (const row of rows) place(row, 0);
  for (const row of rows) depth.delete(row.id);
  return { rows: out, nested, depth };
}
